import axios from 'axios'

/**
 * AIQ Studio — RunPod Serverless API Client
 * Worker: runpod-workers/worker-comfyui v5.8.6
 * Models bundled: FLUX.1 Dev (fp8), ae.safetensors, clip_l, t5xxl_fp8
 *
 * API format:
 *   POST /run  → { input: { workflow: {...comfyui_api_json...} } }
 *   GET  /status/:jobId → { status, output: { images: [{ filename, type, data }] } }
 *
 * Output images come back as base64 strings in output.images[].data
 */

const getClient = () => {
  const apiKey    = import.meta.env.VITE_RUNPOD_API_KEY
  const endpointId = import.meta.env.VITE_RUNPOD_ENDPOINT_ID

  if (!apiKey || apiKey.includes('PASTE_') || !endpointId || endpointId.includes('PASTE_')) {
    throw new Error('RunPod API key or Endpoint ID not configured. Go to Settings.')
  }

  return axios.create({
    baseURL: `https://api.runpod.ai/v2/${endpointId}`,
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  })
}

/**
 * Submit a ComfyUI workflow job
 * @param {Object} workflowJson — ComfyUI API-format workflow (numbered nodes)
 * @param {Array}  inputImages  — optional [{ name, image: 'data:image/png;base64,...' }]
 */
export async function submitJob(workflowJson, inputImages = []) {
  const client = getClient()
  const body = {
    input: {
      workflow: workflowJson,
      ...(inputImages.length > 0 ? { images: inputImages } : {}),
    }
  }
  const response = await client.post('/run', body)
  return response.data  // { id, status }
}

/**
 * Poll job status until complete or failed
 * Returns the full output object on completion
 */
export async function pollJob(jobId, onProgress, intervalMs = 3000, timeoutMs = 300000) {
  const client = getClient()
  const startTime = Date.now()

  return new Promise((resolve, reject) => {
    const poll = async () => {
      // 5-minute timeout
      if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Job timed out after 5 minutes. RunPod worker may be unavailable.'))
        return
      }

      try {
        const response = await client.get(`/status/${jobId}`)
        const { status, output, error } = response.data

        onProgress?.(status, output)

        if (status === 'COMPLETED') {
          resolve(output)
          return
        }
        if (status === 'FAILED' || status === 'CANCELLED') {
          reject(new Error(error || `Job ${status}`))
          return
        }
        // IN_QUEUE, IN_PROGRESS — keep polling
        setTimeout(poll, intervalMs)
      } catch (err) {
        reject(err)
      }
    }
    poll()
  })
}

/**
 * Submit and wait (convenience wrapper)
 */
export async function runJob(workflowJson, onProgress, inputImages = []) {
  const { id } = await submitJob(workflowJson, inputImages)
  return pollJob(id, onProgress)
}

/**
 * Cancel a running job
 */
export async function cancelJob(jobId) {
  const client = getClient()
  return client.post(`/cancel/${jobId}`)
}

/**
 * Health check
 */
export async function getEndpointHealth() {
  try {
    const client = getClient()
    const response = await client.get('/health')
    return { online: true, data: response.data }
  } catch {
    return { online: false }
  }
}

// ─── Response Parser ────────────────────────────────────────────────────────

/**
 * Parse ComfyUI worker output into image data URLs
 * Output format: { images: [{ filename, type: 'base64', data: 'iVBOR...' }] }
 * Returns array of data: URIs ready to display in <img> tags
 */
export function parseImageOutput(output) {
  if (!output?.images || !Array.isArray(output.images)) return []

  return output.images.map(img => {
    if (img.type === 'base64') {
      // Add data URI prefix if not already present
      return img.data.startsWith('data:')
        ? img.data
        : `data:image/png;base64,${img.data}`
    }
    if (img.type === 's3_url') {
      return img.data  // direct URL
    }
    return null
  }).filter(Boolean)
}

// ─── Real ComfyUI Workflow Builders ─────────────────────────────────────────

/**
 * Build FLUX.1 Dev workflow for image generation
 * Uses models bundled in worker-comfyui v5.8.6-flux1-dev:
 *   - flux1-dev.safetensors (fp8_e4m3fn)
 *   - t5xxl_fp8_e4m3fn.safetensors
 *   - clip_l.safetensors
 *   - ae.safetensors
 */
export function buildFluxWorkflow({
  prompt,
  width   = 1024,
  height  = 1024,
  steps   = 20,
  guidance = 3.5,
  seed,
}) {
  const actualSeed = seed || Math.floor(Math.random() * 999999999999)

  // Correct workflow for flux1-dev-fp8 CHECKPOINT (bundled model)
  // Uses CheckpointLoaderSimple — not separate UNET/CLIP/VAE loaders
  // Matches the official flux_dev_checkpoint_example.json exactly
  return {
    "6": {
      "inputs": { "text": prompt, "clip": ["30", 1] },
      "class_type": "CLIPTextEncode",
      "_meta": { "title": "Positive Prompt" }
    },
    "8": {
      "inputs": { "samples": ["31", 0], "vae": ["30", 2] },
      "class_type": "VAEDecode",
      "_meta": { "title": "VAE Decode" }
    },
    "9": {
      "inputs": { "filename_prefix": "ComfyUI", "images": ["8", 0] },
      "class_type": "SaveImage",
      "_meta": { "title": "Save Image" }
    },
    "27": {
      "inputs": { "width": width, "height": height, "batch_size": 1 },
      "class_type": "EmptySD3LatentImage",
      "_meta": { "title": "EmptySD3LatentImage" }
    },
    "30": {
      "inputs": { "ckpt_name": "flux1-dev-fp8.safetensors" },
      "class_type": "CheckpointLoaderSimple",
      "_meta": { "title": "Load Checkpoint" }
    },
    "31": {
      "inputs": {
        "seed": actualSeed,
        "steps": steps,
        "cfg": 1,
        "sampler_name": "euler",
        "scheduler": "simple",
        "denoise": 1,
        "model": ["30", 0],
        "positive": ["35", 0],
        "negative": ["33", 0],
        "latent_image": ["27", 0]
      },
      "class_type": "KSampler",
      "_meta": { "title": "KSampler" }
    },
    "33": {
      "inputs": { "text": "", "clip": ["30", 1] },
      "class_type": "CLIPTextEncode",
      "_meta": { "title": "Negative Prompt" }
    },
    "35": {
      "inputs": { "guidance": guidance, "conditioning": ["6", 0] },
      "class_type": "FluxGuidance",
      "_meta": { "title": "FluxGuidance" }
    }
  }
}

/**
 * Build FLUX.1 Schnell workflow (fast preview — fewer steps, same checkpoint)
 */
export function buildFluxSchnellWorkflow({ prompt, width = 1024, height = 1024, seed }) {
  return buildFluxWorkflow({ prompt, width, height, steps: 10, guidance: 3.5, seed })
}

// Legacy placeholder — kept for backwards compatibility during transition
export function buildLTXWorkflow(params) {
  // LTX-Video requires additional model download — placeholder until Phase 2 video setup
  console.warn('LTX workflow not yet configured — needs LTX-Video 2 model download')
  return null
}

// ─── Cost Estimator ─────────────────────────────────────────────────────────

export function estimateCost(workflowType, params = {}) {
  const COST_PER_SECOND = 0.00012

  const estimates = {
    flux_image:    { seconds: 25,   label: 'FLUX.1 Dev (20 steps)' },
    flux_schnell:  { seconds: 8,    label: 'FLUX.1 Fast (4 steps)' },
    ltx_video:     { seconds: 90,   label: 'LTX-2 Video Clip' },
    museTalk:      { seconds: 30,   label: 'MuseTalk Lip Sync' },
    rife:          { seconds: 10,   label: 'RIFE Interpolation' },
    lora_training: { seconds: 3600, label: 'LoRA Training Run' },
  }

  const est = estimates[workflowType]
  if (!est) return null

  const quantity     = params.quantity || 1
  const totalSeconds = est.seconds * quantity
  const totalCost    = totalSeconds * COST_PER_SECOND

  return {
    label:         est.label,
    quantity,
    totalSeconds,
    estimatedCost: `$${totalCost.toFixed(4)}`,
    costPerUnit:   `$${(est.seconds * COST_PER_SECOND).toFixed(4)}`,
  }
}
